import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { C, getFontFamily, SUBJECT_COLORS, TAB_GRADIENTS, TEXT, ACCENT, CARD, R } from "../constants/colors";
import { getDashboardStats, getStreakDays, getConceptsMastered, getChapterConceptsAttempted } from "../progress";
import { PaywallPopup } from "./PaywallPopup";
import { canUseAiChat, canUseTests } from "../subscription";
import ProgressBar from "./progress-bar";
// COMPETENCY-FULLPAGE-APPLIED: Competency Building is now a full-page route.
// The tab in the Study view acts as a launcher, calling onCompetency() to navigate.

// SUBJECT-IMAGES-APPLIED: hero image lookup for the subject cards on the
// Study screen. Files live in public/images/subjects/. Falls back to the
// existing icon-only chip when the image 404s.
const SUBJECT_IMAGE_MAP = {
  Maths:      "/images/subjects/maths.webp",
  Science:    "/images/subjects/science.webp",
  Geography:  "/images/subjects/geography.webp",
  Economics:  "/images/subjects/economics.webp",
};

// SUBJECT-IMAGES-APPLIED: card with hero image on top + text + progress bar below.
// Each card owns its own load/error state so a single broken image doesn't take
// down the row.
function SubjectCard({ sp, sc, completed, barColor, staggerClass, onTap, isHi, fs, s, SubjIcon }) {
  const imgUrl = SUBJECT_IMAGE_MAP[sp.name];
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const imgRef = useRef(null);
  // CACHED-IMAGE-FIX-APPLIED: when an image is already in the browser cache, its
  // load event can fire before React attaches the onLoad listener — the shimmer
  // would then stay forever. Detect that case on mount via img.complete and
  // promote to loaded/error state directly.
  useEffect(() => {
    const img = imgRef.current;
    if (!img || !imgUrl) return;
    if (img.complete) {
      if (img.naturalWidth > 0) setImgLoaded(true);
      else setImgError(true);
    }
  }, [imgUrl]);
  const HERO_HEIGHT = s(100);
  const pct = sp.count > 0 ? Math.min(100, Math.round((completed / sp.count) * 100)) : 0;

  return (
    <div
      className={`tactile-card animate-scale-in ${staggerClass}`}
      onClick={onTap}
      style={{
        minWidth: 0,
        position: "relative",
        overflow: "hidden",
        borderRadius: R.md,
        background: CARD.bg,
        border: CARD.border,
        boxShadow: "0 2px 14px rgba(140,110,60,0.14)",
        cursor: "pointer",
      }}
    >
      {/* Hero image — full-bleed, no padding */}
      <div style={{ width: "100%", height: HERO_HEIGHT, overflow: "hidden", position: "relative", background: sc.bg }}>
        {/* Shimmer placeholder while the image loads */}
        {imgUrl && !imgLoaded && !imgError && (
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%)",
            backgroundSize: "200% 100%",
            animation: "subjShimmer 1.5s infinite linear",
          }} />
        )}
        {imgUrl && !imgError ? (
          <img
            ref={imgRef}
            src={imgUrl}
            alt={sp.name}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            style={{
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "center",
              display: "block",
              opacity: imgLoaded ? 1 : 0,
              transition: "opacity 0.25s ease",
            }}
          />
        ) : (
          // Fallback: subject-color tinted background with the existing icon
          <div style={{
            width: "100%", height: "100%",
            background: sc.bg,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <SubjIcon name={sp.name} color={sc.c} size={s(36)} />
          </div>
        )}
      </div>

      {/* Text section — padded */}
      <div style={{ padding: `${s(12)}px ${s(14)}px ${s(14)}px` }}>
        <div style={{ fontSize: fs(14), fontWeight: 600, color: "#3D2E15" }}>
          {sp.name}
        </div>
        <div style={{ fontSize: fs(11), fontWeight: 500, color: "#8B7355", marginTop: s(3) }}>
          {completed} of {sp.count} {isHi ? "पूरे" : "done"}
        </div>
        <div style={{
          marginTop: s(10),
          height: s(5),
          borderRadius: 3,
          background: `${barColor}1F`,
          overflow: "hidden",
        }}>
          <div style={{
            width: `${pct}%`,
            height: "100%",
            background: barColor,
            borderRadius: 3,
            transition: "width 0.4s ease",
          }} />
        </div>
      </div>

      {/* Local shimmer keyframe — scoped via single declaration shared across cards. */}
      <style>{`@keyframes subjShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    </div>
  );
}

function useResponsive(designWidth = 390) {
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 390);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const r = Math.min(Math.max(vw / designWidth, 0.85), 1.15);
  const s = useCallback((px) => Math.round(px * r), [r]);
  const fs = useCallback((px) => Math.round(px * r), [r]);
  return { s, fs, r };
}

// CONSOLIDATED-FIX-2-APPLIED: HeroCircle now has 3 states — Resume / Start / Done
// State logic: complete (all mastered) → Done; active chapter → Resume; otherwise → Start
function getHeroState(lastChapter, totalMastered, totalConcepts) {
  if (totalConcepts > 0 && totalMastered >= totalConcepts) return "complete";
  if (lastChapter) return "resume";
  return "start";
}

// Generic sparkle/trophy icon shared by Start and Done states
function SparkleIcon({ color, size = 28, strokeWidth = 2 }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" width={size} height={size}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  );
}

// PREMIUM-PART-3-APPLIED: SVG-based progress ring (replaces conic-gradient + @property)
// Animates strokeDashoffset from full (0%) to target after a 400ms post-mount delay.
function HeroCircle({ lastChapter, lastSubject, conceptCount, conceptsDone, totalMastered, totalConcepts, onTap, ff, isHi, s, SubjIcon, progressSyncing }) {
  const heroState = getHeroState(lastChapter, totalMastered || 0, totalConcepts || 0);

  // Progress ring fill depends on state: resume uses chapter progress, others use overall mastery
  const ringPct =
    heroState === "complete" ? 100 :
    heroState === "resume" && conceptCount > 0 ? Math.min(100, Math.round((conceptsDone / conceptCount) * 100)) :
    0;

  const subjColor = lastSubject ? (SUBJECT_COLORS[lastSubject]?.c || ACCENT.blue) : ACCENT.blue;

  // Drive the animation: mount with 0%, then update to real value after entrance scale-in finishes
  const [animatedPct, setAnimatedPct] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setAnimatedPct(ringPct), 400);
    return () => clearTimeout(t);
  }, [ringPct]);

  // PROGRESS-SYNC-APPLIED: while Firestore is still loading and we have no local
  // chapter yet, render a quiet skeleton instead of flashing "Start your journey"
  // and then flipping to "Resume X" once sync resolves. Placed AFTER all hooks so
  // the hook call order stays constant across renders (react-hooks/rules-of-hooks).
  if (progressSyncing && !lastChapter) {
    const SIZE_SK = 144;
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        padding: `${s(8)}px ${s(20)}px 0`, marginBottom: 24,
      }}>
        <style>{`@keyframes hero-skel-pulse { 0%, 100% { opacity: 0.45; } 50% { opacity: 0.85; } }`}</style>
        <div style={{
          width: 120, height: 10, borderRadius: 5,
          background: "rgba(15,27,51,0.10)",
          animation: "hero-skel-pulse 1.4s ease-in-out infinite",
          marginBottom: 16,
        }} />
        <div style={{
          width: SIZE_SK, height: SIZE_SK, borderRadius: "50%",
          background: "rgba(255,255,255,0.55)",
          boxShadow: "0 4px 20px rgba(140,110,60,0.10)",
          animation: "hero-skel-pulse 1.4s ease-in-out infinite",
        }} />
        <div style={{
          width: 180, height: 16, borderRadius: 4,
          background: "rgba(15,27,51,0.10)",
          animation: "hero-skel-pulse 1.4s ease-in-out infinite",
          marginTop: 18,
        }} />
        <div style={{
          width: 120, height: 10, borderRadius: 4,
          background: "rgba(15,27,51,0.08)",
          animation: "hero-skel-pulse 1.4s ease-in-out infinite",
          marginTop: 8,
        }} />
      </div>
    );
  }

  const SIZE = 140;
  const RADIUS = 66;
  const STROKE = 5;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset = CIRCUMFERENCE * (1 - animatedPct / 100);
  const ringColor = heroState === "complete" ? "#E8A817" : "#1A7A52";

  // Caption text per state
  const caption =
    heroState === "resume" ? (isHi ? "जहाँ छोड़ा था वहाँ से शुरू करें" : "Continue where you left off") :
    heroState === "complete" ? (isHi ? "बधाई हो" : "Congratulations") :
    (isHi ? "स्वागत है" : "Welcome");

  // Inner circle label
  const innerLabel =
    heroState === "resume" ? (isHi ? "फिर शुरू करें" : "Resume") :
    heroState === "complete" ? (isHi ? "पूर्ण" : "Done") :
    (isHi ? "शुरू करें" : "Start");

  // HERO-PREMIUM-APPLIED: subtle gradient (15% → 6%) per state for a 3D feel.
  const iconBgGradient =
    heroState === "resume"   ? `linear-gradient(135deg, ${subjColor}26 0%, ${subjColor}0F 100%)` :
    heroState === "complete" ? "linear-gradient(135deg, rgba(232,168,23,0.15) 0%, rgba(232,168,23,0.06) 100%)" :
    "linear-gradient(135deg, rgba(26,122,82,0.15) 0%, rgba(26,122,82,0.06) 100%)";

  // Below-circle headline + subtitle
  const headline =
    heroState === "resume" ? lastChapter :
    heroState === "complete" ? (isHi ? "आपने सब कुछ पूरा कर लिया" : "You've mastered everything") :
    (isHi ? "अपनी यात्रा शुरू करें" : "Start your journey");

  // Resume subtitle: show real fraction when we know the total, otherwise just the chapter's subject —
  // never show "of ?" (decorated empty state) per the no-fake-data rule.
  const resumeSubtitle = (() => {
    const subj = lastSubject || "Maths";
    if (conceptCount && conceptCount > 0) {
      return isHi
        ? `${subj} · ${conceptCount} में से ${conceptsDone} अवधारणाएं`
        : `${subj} · ${conceptsDone} of ${conceptCount} concepts done`;
    }
    return subj;
  })();
  const subtitle =
    heroState === "resume" ? resumeSubtitle :
    heroState === "complete" ? `${totalMastered} of ${totalConcepts} concepts done` :
    (isHi ? "नीचे कोई विषय चुनें" : "Choose any subject below");

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: `${s(8)}px ${s(20)}px 0`, marginBottom: 24,
    }}>
      {/* Caption — on blue gradient, dark navy for 8:1+ contrast */}
      <div className="animate-in stagger-1" style={{
        fontSize: 11, fontWeight: 600, letterSpacing: "0.8px", textTransform: "uppercase",
        color: "#1A2744", marginBottom: 12, fontFamily: ff,
      }}>
        {caption}
      </div>

      {/* Hero circle — tappable, scales in, scales on tap */}
      <div
        onClick={onTap}
        className="tactile-hero animate-scale-in stagger-2"
        style={{
          position: "relative", width: SIZE, height: SIZE, cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <svg
          width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}
          style={{ position: "absolute", top: 0, left: 0, transform: "rotate(-90deg)", pointerEvents: "none" }}
        >
          {/* Track */}
          <circle cx={SIZE/2} cy={SIZE/2} r={RADIUS} fill="none" stroke="rgba(15,27,51,0.12)" strokeWidth={STROKE} />
          {/* Progress (animated via stroke-dashoffset transition); color shifts gold when complete */}
          <circle
            cx={SIZE/2} cy={SIZE/2} r={RADIUS}
            fill="none" stroke={ringColor} strokeWidth={STROKE} strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.25,0.46,0.45,0.94), stroke 0.4s ease" }}
          />
        </svg>

        {/* Badge only for resume state with actual progress */}
        {heroState === "resume" && ringPct > 0 && (
          <span style={{
            position: "absolute", top: -2, right: -4, zIndex: 2,
            background: "#1A7A52", color: "#fff",
            fontSize: 11, fontWeight: 700, padding: "3px 9px",
            borderRadius: 10, boxShadow: "0 2px 8px rgba(26,122,82,0.35)",
            fontFamily: ff,
          }}>
            {ringPct}%
          </span>
        )}

        {/* Inner — pure white against the blue gradient */}
        <div style={{
          position: "absolute", top: STROKE, left: STROKE,
          width: SIZE - 2 * STROKE, height: SIZE - 2 * STROKE, zIndex: 1,
          background: "#FFFFFF", borderRadius: "50%",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 20px rgba(140,110,60,0.18)",
        }}>
          {/* HERO-PREMIUM-APPLIED: 52px gradient chip with inset highlight + ambient shadow */}
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: iconBgGradient,
            display: "flex", alignItems: "center", justifyContent: "center",
            marginBottom: 6,
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.06), 0 2px 8px rgba(140,110,60,0.12)",
          }}>
            {heroState === "resume" && <SubjIcon name={lastSubject || "Maths"} color={subjColor} size={28} strokeWidth={2.5} />}
            {heroState === "start" && <SparkleIcon color="#1A7A52" size={28} strokeWidth={2.5} />}
            {heroState === "complete" && <SparkleIcon color="#E8A817" size={28} strokeWidth={2.5} />}
          </div>
          {/* HERO-PREMIUM-APPLIED: stronger label — 12/800/1px tracking */}
          <span style={{
            fontSize: 12, fontWeight: 800, letterSpacing: "1px", textTransform: "uppercase",
            color: heroState === "complete" ? "#E8A817" : "#1A7A52", fontFamily: ff,
          }}>
            {innerLabel}
          </span>
        </div>
      </div>

      {/* Chapter text below — dark navy on blue for high contrast */}
      <div className="animate-in stagger-3" style={{ textAlign: "center", marginTop: 14 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", lineHeight: 1.2, fontFamily: ff }}>
          {headline}
        </div>
        <div style={{ fontSize: 12, fontWeight: 500, color: "#1A2744", marginTop: 3, fontFamily: ff }}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}

// Normalize chapter names so visit logs and curriculum match across dash variants
// (hyphen-minus / en-dash / em-dash) and stray whitespace.
function normalizeChapterName(s) {
  return (s || "")
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function getChapterConceptCount(curriculumData, profile, chapterName) {
  if (!curriculumData || !profile || !chapterName) return null;
  const board = profile.board || "CBSE";
  const cls = String(profile.studentClass || 10);
  const classData = curriculumData[board]?.[cls] || curriculumData[cls];
  if (!classData) return null;
  const target = normalizeChapterName(chapterName);
  for (const subj of Object.values(classData)) {
    const ch = subj.chapters?.find(c => normalizeChapterName(c.name) === target);
    if (ch) return ch.concepts?.length || null;
  }
  return null;
}

// Subject color helper for micro-fact pills
const SUBJECT_DOT_COLORS = {
  Science: "#10B981", Maths: "#D4880A", History: "#E85D3A",
  Geography: "#3B82F6", Civics: "#5A7090", Economics: "#8B5CF6",
};

// Subject fill colors for progress bars (ProgressBar component computes track automatically)
const SUBJECT_BAR_COLORS = {
  Maths: "#F59E0B", Science: "#10B981", Geography: "#3B82F6",
  Economics: "#8B5CF6", History: "#C2702A", Civics: "#5A7090",
};

export function ModePage({ onMode, onResumeChapter, profile, lang, setLang, onDashboard, onFlashCards, onExamCorner, onCompetency, onBites, curriculumData, onSettings, onUpgrade, initialTab, initialReviseView, onModeConsumed, onClassChange, progressSyncing }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const { s, fs } = useResponsive();
  // TEST-FLOW-BACK-NAV-APPLIED: honor initial tab/view from parent (e.g. returning from Exam Corner → Revise>Exam)
  const [activeTab, setActiveTab] = useState(initialTab || "study");
  const [studyView, setStudyView] = useState("chapters");
  const [reviseView, setReviseView] = useState(initialReviseView || "flash");
  useEffect(() => { if (initialTab || initialReviseView) onModeConsumed?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [paywallType, setPaywallType] = useState(null);
  // CLASS-10-ONLY-APPLIED: classPickerOpen state removed — class picker no longer exists.
  const [microFacts, setMicroFacts] = useState([]);
  // REVISE-EXAM-STEP-2-APPLIED: subject selection for inline Exam Corner Test Yourself section
  const [examSubj, setExamSubj] = useState(null);
  // HEADER-TOOLTIP-APPLIED: tap (mobile) or hover (desktop title) reveals what each header
  // pill means — streak = consecutive study days, the gold "days" badge = total active days.
  const [headerTip, setHeaderTip] = useState(null);
  const tipTimerRef = useRef(null);
  const toggleTip = (id) => {
    setHeaderTip((cur) => (cur === id ? null : id));
    if (tipTimerRef.current) clearTimeout(tipTimerRef.current);
    tipTimerRef.current = setTimeout(() => setHeaderTip(null), 3500);
  };

  useEffect(() => {
    fetch("/data/micro-concepts.json")
      .then(r => r.json())
      .then(data => {
        const cls = String(profile?.studentClass || 10);
        const facts = data?.[cls] || data?.["10"] || [];
        setMicroFacts(facts.sort(() => Math.random() - 0.5).slice(0, 8));
      }).catch(() => {});
  }, [profile?.studentClass]);

  // Build subject pills from curriculumData
  // Build subject pills from curriculumData — structure: { CBSE: { "10": { Maths: {...} } } }
  const subjectPills = [];
  if (curriculumData && profile) {
    const board = profile.board || "CBSE";
    const cls = String(profile.studentClass || 10);
    const classData = curriculumData[board]?.[cls] || curriculumData[cls];
    if (classData) {
      Object.entries(classData).forEach(([subj, data]) => {
        subjectPills.push({ name: subj, count: data.chapters?.length || 0 });
      });
    }
  }

  // Stats
  const stats = getDashboardStats(profile?.studentClass);
  const daysActive = stats?.daysActive || 0;
  const streak = getStreakDays(stats?.activeDaysList);
  const conceptsMastered = getConceptsMastered(profile?.studentClass);
  const recentChapter = stats?.recentChapters?.[0];
  const lastChapter = recentChapter?.chapter;
  const lastSubject = recentChapter?.subject;
  const conceptCount = lastChapter ? getChapterConceptCount(curriculumData, profile, lastChapter) : null;
  // Real per-chapter progress — count concepts in the last chapter that have at least one quiz attempt.
  const conceptsDone = (lastSubject && lastChapter)
    ? getChapterConceptsAttempted(profile?.studentClass, lastSubject, lastChapter)
    : 0;
  const totalChapters = subjectPills.reduce((sum, sp) => sum + sp.count, 0) || 0;
  const totalCompleted = stats?.chaptersCompleted || 0;

  // STUDY-REDESIGN: greeting block removed — no longer needed

  // PREMIUM-PART-2-APPLIED: staggered entrance now handled by CSS .animate-in/.stagger-* classes
  const fadeUp = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };

  // Subject icon SVG
  const SubjIcon = ({ name, color, size = 16, strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {name === "Maths" && <><path d="M4 4h16M4 12h16M4 20h8"/><circle cx="18" cy="18" r="3"/></>}
      {name === "Science" && <><path d="M9 3h6v5l4 9H5l4-9V3z"/><path d="M9 3h6"/></>}
      {name === "Geography" && <><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2c-3 3-3 9 0 18M12 2c3 3 3 9 0 18"/></>}
      {name === "Economics" && <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></>}
      {name === "History" && <><circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 2"/></>}
      {name === "Civics" && <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></>}
    </svg>
  );

  // Subject color for last chapter
  const lastSubjColor = lastSubject ? (SUBJECT_COLORS[lastSubject]?.c || ACCENT.blue) : ACCENT.blue;

  // HEADER-TOOLTIP-APPLIED: per-pill explanations + a shared dark bubble style.
  const TIP = {
    class: isHi ? "आप कक्षा 10 CBSE कोर्स पर हैं।" : "You're on the Class 10 CBSE course.",
    streak: isHi ? "स्ट्रीक: लगातार कितने दिन पढ़े। रोज़ Samajh खोलो ताकि बनी रहे।" : "Streak: days in a row you've studied. Open Samajh daily to keep it going.",
    days: isHi ? "सक्रिय दिन: आपने Samajh पर कुल कितने दिन पढ़ा।" : "Active days: total days you've studied on Samajh.",
  };
  const tipBubble = (align) => ({
    position: "absolute", top: "100%", marginTop: 6, [align]: 0,
    background: "rgba(15,27,51,0.95)", color: "#FFFFFF",
    fontSize: 11, fontWeight: 500, lineHeight: 1.4,
    padding: "8px 10px", borderRadius: 8, width: "max-content", maxWidth: 190,
    boxShadow: "0 6px 18px rgba(140,110,60,0.28)",
    zIndex: 1001, fontFamily: ff, whiteSpace: "normal", textAlign: "left",
  });

  return (
    <div style={{
      // HOME-SCALE-APPLIED: shrink the whole home page ~8% uniformly (text, icons,
      // hero, cards, bottom nav) via zoom. Chrome and the Capacitor WebView are both
      // Blink, so zoom renders identically. minHeight is divided by the same factor
      // so the scaled root still fills the viewport (no gap below the gradient).
      fontFamily: ff, minHeight: "calc(100vh / 0.92)", zoom: 0.92,
      display: "flex", flexDirection: "column",
      background: TAB_GRADIENTS[activeTab] || TAB_GRADIENTS.study,
      transition: "background 0.5s ease",
    }}>

      {/* ═══ HEADER ═══ CONSOLIDATED-FIX-1-APPLIED: header wrapper gets position:relative + zIndex 1000
          so the class dropdown menu (inside) stays above the fixed backdrop (zIndex 99).
          Root cause: animate-in's CSS animation applies transform, creating a stacking context at z-auto;
          without explicit zIndex the fixed backdrop overlapped everything inside the header. */}
      <div className="animate-in stagger-0" style={{
        padding: `${s(14)}px ${s(20)}px ${s(6)}px`,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
        position: "relative", zIndex: 1000,
      }}>
        {/* LEFT: Class label (static — Samajh is Class 10 CBSE only).
            CLASS-10-ONLY-APPLIED: picker removed since the app supports only Class 10.
            HEADER-TOOLTIP-APPLIED: tap/hover reveals what the pill means. */}
        <div
          onClick={() => toggleTip("class")}
          onMouseEnter={() => setHeaderTip("class")}
          onMouseLeave={() => setHeaderTip(null)}
          title={TIP.class}
          style={{
            position: "relative",
            minHeight: 44, display: "inline-flex", alignItems: "center",
            fontSize: 13, fontWeight: 600, color: "#0F1B33",
            background: "rgba(255,255,255,0.25)", borderRadius: 8,
            padding: "8px 14px", fontFamily: ff,
            cursor: "pointer", WebkitTapHighlightColor: "transparent",
          }}>
          {isHi ? "कक्षा 10" : "Class 10"}
          {headerTip === "class" && <div style={tipBubble("left")}>{TIP.class}</div>}
        </div>

        {/* RIGHT: streak pill + coin pill */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {/* Streak pill */}
          <div
            onClick={() => toggleTip("streak")}
            onMouseEnter={() => setHeaderTip("streak")}
            onMouseLeave={() => setHeaderTip(null)}
            title={TIP.streak}
            style={{
              position: "relative",
              minHeight: 44, display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 12, fontWeight: 600, color: "#0F1B33",
              background: "rgba(255,255,255,0.2)", borderRadius: 8,
              padding: "8px 12px", fontFamily: ff,
              cursor: "pointer", WebkitTapHighlightColor: "transparent",
            }}>
            {streak > 0
              ? <>{isHi ? `🔥 ${streak}-दिन स्ट्रीक` : `🔥 ${streak}-day streak`}</>
              : <span>{isHi ? "स्ट्रीक शुरू करो!" : "Start your streak!"}</span>
            }
            {headerTip === "streak" && <div style={tipBubble("right")}>{TIP.streak}</div>}
          </div>

          {/* Coin pill — shows total active days */}
          <div
            onClick={() => toggleTip("days")}
            onMouseEnter={() => setHeaderTip("days")}
            onMouseLeave={() => setHeaderTip(null)}
            title={TIP.days}
            style={{
              position: "relative",
              minHeight: 44, display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 12, fontWeight: 600, color: "#0F1B33",
              background: "rgba(255,255,255,0.2)", borderRadius: 8,
              padding: "8px 10px", fontFamily: ff,
              cursor: "pointer", WebkitTapHighlightColor: "transparent",
            }}>
            <div style={{ width: 8, height: 8, background: "#E8A817", borderRadius: "50%" }} />
            <span>{daysActive || 0}</span>
            {headerTip === "days" && <div style={tipBubble("right")}>{TIP.days}</div>}
          </div>
        </div>
      </div>
      {/* CLASS-10-ONLY-APPLIED: dropdown backdrop removed with the picker. */}

      {/* ═══ SCROLL ═══ */}
      <div style={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch" }}>

        {/* ── Study tab content ── */}
        {activeTab === "study" && (<>

          {/* ═══ HERO — 3 states (Resume / Start / Done) CONSOLIDATED-FIX-2-APPLIED ═══ */}
          {(() => {
            // Compute total concepts across entire curriculum for current class+board
            let totalConceptsInCurriculum = 0;
            if (curriculumData && profile) {
              const board = profile.board || "CBSE";
              const cls = String(profile.studentClass || 10);
              const classData = curriculumData[board]?.[cls] || curriculumData[cls];
              if (classData) {
                Object.values(classData).forEach(subj => {
                  (subj.chapters || []).forEach(ch => {
                    totalConceptsInCurriculum += (ch.concepts?.length || 0);
                  });
                });
              }
            }
            return (
              <HeroCircle
                lastChapter={lastChapter}
                lastSubject={lastSubject}
                conceptCount={conceptCount}
                conceptsDone={conceptsDone}
                totalMastered={conceptsMastered}
                totalConcepts={totalConceptsInCurriculum}
                progressSyncing={progressSyncing}
                onTap={() => {
                  // Resume tap → deep-link into the active chapter; otherwise → chapter list flow
                  if (lastChapter && lastSubject && onResumeChapter) {
                    onResumeChapter(lastSubject, lastChapter);
                  } else {
                    onMode("explain");
                  }
                }}
                ff={ff}
                isHi={isHi}
                s={s}
                SubjIcon={SubjIcon}
              />
            );
          })()}

          {/* ── Explore section — PREMIUM-PART-1 + PREMIUM-PART-2 APPLIED ── */}
          <div className="animate-in stagger-4" style={{ padding: `${s(14)}px ${s(20)}px 0` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: s(6) }}>
              {/* GRADIENT-ZONE-SHIFT-APPLIED: heading renamed "Explore chapters" → "Explore subjects" */}
              <span style={{ fontSize: 17, fontWeight: 700, color: "#0F1B33", fontFamily: ff }}>
                {isHi ? "विषय देखें" : "Explore subjects"}
              </span>
              <div className="tactile-link" onClick={() => onMode("explain")} style={{ padding: "10px 0 10px 12px" }}>
                {/* CONTRAST-FIX 3: View all 4.6:1 */}
                <div style={{ minHeight: 44, display: "flex", alignItems: "center", fontSize: 12, fontWeight: 500, color: "#42506F", fontFamily: ff }}>
                  {isHi ? "सब देखें" : "View all"}
                </div>
              </div>
            </div>
            {/* TAB-REDESIGN-APPLIED: three equal pills with shimmering colored borders.
                Each tab self-identifies by color even when inactive — sky for Chapters,
                violet for Bites, terracotta for Competency. Badges float outside the pill. */}
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 8, alignItems: "center", marginBottom: s(4),
            }}>
              {[
                { id: "chapters",   label: isHi ? "अध्याय" : "Chapters",     badge: null,   shimmer: "tab-pill-chapters",   color: "#38BDF8", tintBg: "rgba(56,189,248,0.08)"  },
                { id: "bites",      label: isHi ? "बाइट्स" : "Bites",        badge: "new",  shimmer: "tab-pill-bites",      color: "#8B5CF6", tintBg: "rgba(139,92,246,0.08)" },
                { id: "competency", label: "Competency",                     badge: "new",  shimmer: "tab-pill-competency", color: "#B5491C", tintBg: "rgba(181,73,28,0.08)"  },
              ].map(({ id, label, badge, shimmer, color, tintBg }) => {
                const active = studyView === id;
                return (
                  <div key={id} className={`tactile-pill ${shimmer}`} onClick={() => {
                    // TEASER-APPLIED: Bites & Competency now show a premium inline teaser;
                    // the teaser's "Start" button launches the full-page route.
                    setStudyView(id);
                  }} style={{
                    position: "relative", minHeight: 36,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "7px 6px", borderRadius: 999, cursor: "pointer",
                    border: `1px solid ${active ? color : color + "8C"}`, // 8C = ~55% alpha
                    background: active ? tintBg : "#FFFFFF",
                    boxSizing: "border-box", width: "100%",
                    transition: "background 0.2s, border-color 0.2s",
                  }}>
                    <span style={{
                      fontSize: 12, fontWeight: active ? 700 : 500,
                      color: active ? "#0F1B33" : "#1A2744",
                      fontFamily: ff, transition: "all 0.2s",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{label}</span>
                    {badge === "soon" && (
                      <span style={{
                        position: "absolute", top: -7, right: -6,
                        fontSize: 9, fontWeight: 800, letterSpacing: "0.06em",
                        color: "#FFFFFF", background: "#8B5CF6",
                        padding: "2px 6px", borderRadius: 999, textTransform: "uppercase",
                        lineHeight: 1, whiteSpace: "nowrap",
                        border: "1.5px solid #FFFFFF",
                        boxShadow: "0 1px 3px rgba(15,27,51,0.12)",
                        fontFamily: ff, zIndex: 2,
                      }}>
                        {isHi ? "जल्द" : "Soon"}
                      </span>
                    )}
                    {badge === "new" && (
                      <span style={{
                        position: "absolute", top: -7, right: -6,
                        fontSize: 9, fontWeight: 800, letterSpacing: "0.06em",
                        color: "#FFFFFF", background: "#B5491C",
                        padding: "2px 6px", borderRadius: 999, textTransform: "uppercase",
                        lineHeight: 1, whiteSpace: "nowrap",
                        border: "1.5px solid #FFFFFF",
                        boxShadow: "0 1px 3px rgba(15,27,51,0.12)",
                        fontFamily: ff, zIndex: 2,
                      }}>
                        {isHi ? "नया" : "New"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Aggregate mastery bar — STUDY-REDESIGN: dark navy text */}
            <div style={{ marginTop: 4, marginBottom: 8 }}>
              {/* CONTRAST-FIX 5: mastered count 4.6:1 */}
              <div style={{ fontSize: 12, fontWeight: 500, color: "#42506F", fontFamily: ff }}>
                {totalCompleted} of {totalChapters || 0} {isHi ? "पूरे" : "mastered"}
              </div>
              <ProgressBar
                completed={totalCompleted}
                total={totalChapters || 1}
                color="#38BDF8"
                style={{ marginTop: 6 }}
              />
            </div>
          </div>

          {/* Tab content — PREMIUM-PART-1 + PREMIUM-PART-2 APPLIED */}
          <div style={{ padding: `0 ${s(20)}px ${s(8)}px` }}>
            <AnimatePresence mode="wait">
              {studyView === "chapters" && (
                <motion.div key="chapters" {...fadeUp}>
                  {subjectPills.length === 0 ? (
                    <div style={{
                      textAlign: "center", padding: "24px 16px",
                      color: "#1A2744", opacity: 0.55, fontSize: 14, fontFamily: ff,
                    }}>
                      <span style={{ fontSize: 24 }}>📚</span>
                      <p style={{ marginTop: 8 }}>{isHi ? "इस कक्षा के अध्याय जल्द आ रहे हैं!" : "Chapters coming soon!"}</p>
                    </div>
                  ) : (
                  // GRID-FIX-APPLIED: CSS Grid guarantees 2 columns regardless of card content width
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: s(8) }}>
                    {subjectPills.map((sp, i) => {
                      const sc = SUBJECT_COLORS[sp.name] || SUBJECT_COLORS.Maths;
                      const completed = stats?.subjectProgress?.[sp.name]?.completed || 0;
                      // Each row of 2 cards staggers together; row 1 = stagger-5, row 2 = stagger-6
                      const staggerClass = i < 2 ? "stagger-5" : "stagger-6";
                      const barColor = SUBJECT_BAR_COLORS[sp.name] || sc.c;
                      // SUBJECT-IMAGES-APPLIED: hero image on top + text-below layout
                      return (
                        <SubjectCard
                          key={i}
                          sp={sp}
                          sc={sc}
                          completed={completed}
                          barColor={barColor}
                          staggerClass={staggerClass}
                          onTap={() => onMode("explain", sp.name)}
                          isHi={isHi}
                          fs={fs}
                          s={s}
                          SubjIcon={SubjIcon}
                        />
                      );
                    })}
                  </div>
                  )}
                </motion.div>
              )}

              {/* BITES-TEASER: TEASER-REDESIGN-APPLIED — quirky one-liner + single Start CTA,
                  dark glass surface (inset top highlight + blurred glow orbs) for depth. */}
              {studyView === "bites" && (
                <motion.div key="bites" {...fadeUp}>
                  <div style={{
                    position: "relative", borderRadius: R.lg, overflow: "hidden",
                    background: C.bitesBg,
                    border: "1px solid rgba(255,255,255,0.10)",
                    boxShadow: "0 14px 36px rgba(20,20,30,0.45), inset 0 1px 0 rgba(255,255,255,0.10)",
                    padding: `${s(24)}px ${s(22)}px ${s(22)}px`,
                  }}>
                    {/* depth: violet glow top-right + warm History glow bottom-left */}
                    <div style={{ position: "absolute", top: -50, right: -40, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246,0.30), transparent 70%)", pointerEvents: "none" }} />
                    <div style={{ position: "absolute", bottom: -60, left: -50, width: 160, height: 160, borderRadius: "50%", background: "radial-gradient(circle, rgba(232,93,58,0.14), transparent 70%)", pointerEvents: "none" }} />

                    <div style={{ position: "relative", zIndex: 1 }}>
                      {/* icon chip — glassy violet with inset highlight */}
                      <div style={{
                        width: s(52), height: s(52), borderRadius: s(16),
                        background: "linear-gradient(135deg, rgba(139,92,246,0.30), rgba(139,92,246,0.10))",
                        border: "1px solid rgba(139,92,246,0.45)",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 6px 16px rgba(139,92,246,0.25)",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        marginBottom: s(16),
                      }}>
                        <svg width={s(24)} height={s(24)} viewBox="0 0 24 24" fill="#C4B5FD"><polygon points="7 4 20 12 7 20"/></svg>
                      </div>

                      <div style={{ fontSize: fs(22), fontWeight: 700, color: "white", letterSpacing: "-0.02em", marginBottom: s(6) }}>
                        {isHi ? "बाइट्स" : "Bites"}
                      </div>
                      <div style={{ fontSize: fs(14), color: "rgba(255,255,255,0.62)", lineHeight: 1.45, maxWidth: 320, marginBottom: s(20) }}>
                        {isHi ? "इतिहास, स्वाइप में। Shorts जैसा, पर यहाँ दिमाग़ तेज़ होता है।" : "History in swipes. Like Shorts, except you walk away smarter."}
                      </div>

                      <button onClick={() => onBites?.()} className="tactile-pill" style={{
                        width: "100%", border: "none", cursor: "pointer", borderRadius: s(14),
                        padding: `${s(14)}px`, fontFamily: ff, fontSize: fs(15), fontWeight: 700, color: "white",
                        background: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)",
                        boxShadow: "0 8px 20px rgba(139,92,246,0.42), inset 0 1px 0 rgba(255,255,255,0.25)",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: s(8),
                      }}>
                        <svg width={s(16)} height={s(16)} viewBox="0 0 24 24" fill="white"><polygon points="7 4 20 12 7 20"/></svg>
                        {isHi ? "शुरू करो" : "Start"}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* COMPETENCY-TEASER: TEASER-REDESIGN-APPLIED — quirky one-liner + single Start CTA,
                  light cream-glass surface (inset top highlight + warm glow) for depth. */}
              {studyView === "competency" && (
                <motion.div key="competency" {...fadeUp}>
                  <div style={{
                    position: "relative", borderRadius: R.lg, overflow: "hidden",
                    background: "linear-gradient(160deg, #FFFFFF 0%, #FFF4E8 100%)",
                    border: "1px solid rgba(181,73,28,0.18)",
                    boxShadow: "0 12px 30px rgba(140,110,60,0.18), inset 0 1px 0 rgba(255,255,255,0.8)",
                    padding: `${s(24)}px ${s(22)}px ${s(22)}px`,
                  }}>
                    {/* depth: warm terracotta glow top-right */}
                    <div style={{ position: "absolute", top: -50, right: -40, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(181,73,28,0.16), transparent 70%)", pointerEvents: "none" }} />

                    <div style={{ position: "relative", zIndex: 1 }}>
                      {/* icon chip — terracotta gradient with inset highlight */}
                      <div style={{
                        width: s(52), height: s(52), borderRadius: s(16),
                        background: "linear-gradient(135deg, #B5491C 0%, #E8946E 100%)",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35), 0 6px 16px rgba(181,73,28,0.30)",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        marginBottom: s(16),
                      }}>
                        <svg width={s(24)} height={s(24)} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                      </div>

                      <div style={{ fontSize: fs(22), fontWeight: 700, color: "#3D2E15", letterSpacing: "-0.02em", marginBottom: s(6) }}>
                        {isHi ? "योग्यता" : "Competency"}
                      </div>
                      <div style={{ fontSize: fs(14), color: "#7C5A3A", lineHeight: 1.45, maxWidth: 320, marginBottom: s(20) }}>
                        {isHi ? "वो सवाल जो बोर्ड असल में पूछता है, जिन्हें सिर्फ़ रटकर हल नहीं कर सकते।" : "The questions your board exam actually asks, the kind rote learning can't crack."}
                      </div>

                      <button onClick={() => onCompetency?.()} className="tactile-pill" style={{
                        width: "100%", border: "none", cursor: "pointer", borderRadius: s(14),
                        padding: `${s(14)}px`, fontFamily: ff, fontSize: fs(15), fontWeight: 700, color: "white",
                        background: "linear-gradient(135deg, #B5491C 0%, #9A3D17 100%)",
                        boxShadow: "0 8px 20px rgba(181,73,28,0.32), inset 0 1px 0 rgba(255,255,255,0.25)",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: s(8),
                      }}>
                        {isHi ? "अभ्यास शुरू करो" : "Start practising"}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── "Did you know?" — auto-scrolling ticker (PART-1 + PART-2 + DYK-TICKER) ── */}
          {microFacts.length > 0 && (
            <div className="animate-in stagger-6" style={{ padding: `${s(6)}px 0 ${s(112)}px` }}>
              {/* CONTRAST-FIX 6: Did you know heading 4.6:1 */}
              <div style={{ fontSize: 13, fontWeight: 600, color: "#42506F", marginTop: 20, marginBottom: 8, fontFamily: ff, padding: `0 ${s(20)}px` }}>
                {isHi ? "क्या आप जानते हैं?" : "Did you know?"}
              </div>
              {/* Ticker: outer mask hides overflow; inner row scrolls -50% on a loop because
                  the facts are duplicated so the second copy slides in seamlessly behind the first. */}
              <style>{`
                @keyframes dyk-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
                .dyk-ticker:hover .dyk-track { animation-play-state: paused; }
                @media (prefers-reduced-motion: reduce) { .dyk-track { animation: none !important; } }
              `}</style>
              <div className="dyk-ticker" style={{
                overflow: "hidden",
                WebkitMaskImage: "linear-gradient(to right, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)",
                maskImage: "linear-gradient(to right, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)",
              }}>
                <div className="dyk-track" style={{
                  display: "inline-flex", gap: s(8), paddingBottom: s(4),
                  animation: `dyk-marquee ${Math.max(20, microFacts.slice(0, 5).length * 7)}s linear infinite`,
                  willChange: "transform",
                }}>
                  {/* Render the visible facts twice so the loop is seamless. The keys are
                      suffixed (a/b) to keep React's reconciliation stable. */}
                  {[...microFacts.slice(0, 5), ...microFacts.slice(0, 5)].map((fact, i) => {
                    const dotColor = SUBJECT_DOT_COLORS[fact.subject] || "#94A3B8";
                    const isClone = i >= microFacts.slice(0, 5).length;
                    return (
                      <div key={`${i}-${isClone ? "b" : "a"}`} className="tactile-pill" aria-hidden={isClone || undefined} style={{
                        flexShrink: 0, display: "flex", alignItems: "center", gap: s(8),
                        background: "#FFFFFF", borderRadius: 12, padding: "10px 14px",
                        boxShadow: "0 2px 14px rgba(140,110,60,0.14)", border: CARD.border, whiteSpace: "nowrap",
                      }}>
                        <div style={{ width: s(6), height: s(6), borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
                        <span style={{ fontSize: fs(11), color: TEXT.secondary, fontWeight: 400 }}>
                          {fact.text} <b style={{ color: TEXT.primary, fontWeight: 700 }}>{fact.key}</b>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {microFacts.length === 0 && <div style={{ paddingBottom: s(112) }} />}
        </>)}

        {/* AI-TUTOR-STEP-1A-APPLIED: landing screen removed. Practice tab routes directly to TutorApp. */}

        {/* ═══ REVISE TAB ═══ */}
        {activeTab === "revise" && (<>
          <div style={{ padding: `0 ${s(20)}px` }}>
            <div style={{
              display: "flex", background: "rgba(255,255,255,0.24)", borderRadius: R.md, padding: s(3), marginBottom: s(12),
            }}>
              {[["flash", isHi ? "फ्लैश कार्ड" : "Flash Cards"], ["exam", isHi ? "परीक्षा कोना" : "Exam Corner"]].map(([id, label]) => (
                <div key={id} onClick={() => setReviseView(id)} style={{
                  flex: 1, textAlign: "center", padding: `${s(9)}px 0`, borderRadius: s(11),
                  fontSize: fs(12), fontWeight: 600, cursor: "pointer", transition: "all 0.2s",
                  background: reviseView === id ? "white" : "transparent",
                  color: reviseView === id ? ACCENT.green : "rgba(255,255,255,0.92)",
                  boxShadow: reviseView === id ? CARD.shadowSubtle : "none",
                  fontFamily: ff,
                }}>{label}</div>
              ))}
            </div>
          </div>

          <div style={{ padding: `0 ${s(20)}px ${s(112)}px` }}>
            <AnimatePresence mode="wait">
              {reviseView === "flash" && (
                <motion.div key="flash" {...fadeUp}>
                  {subjectPills.map((sp, i) => {
                    const sc = SUBJECT_COLORS[sp.name] || SUBJECT_COLORS.Maths;
                    const cls = String(profile?.studentClass || 10);
                    const chapters = curriculumData?.[cls]?.[sp.name]?.chapters || [];
                    const chapterNames = chapters.slice(0, 3).map(c => c.name);
                    const locked = i >= 3;
                    return (
                      <div key={i} className="card-tactile" onClick={() => {
                        if (locked) setPaywallType("tests");
                        else onFlashCards?.();
                      }} style={{
                        background: CARD.bg, borderRadius: R.md, padding: `${s(13)}px ${s(14)}px`, marginBottom: s(8),
                        borderLeft: `3px solid ${sc.c}`, boxShadow: CARD.shadow, border: CARD.border,
                        opacity: locked ? 0.5 : 1, borderLeftColor: sc.c, borderLeftWidth: 3, borderLeftStyle: "solid",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: s(12) }}>
                          <div style={{
                            width: s(36), height: s(36), borderRadius: R.sm,
                            background: sc.bg, display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <svg width={s(16)} height={s(16)} viewBox="0 0 24 24" fill="none" stroke={sc.c} strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M12 4v16"/></svg>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: fs(14), fontWeight: 600, color: TEXT.primary }}>{sp.name}</div>
                            <div style={{ fontSize: fs(10), color: TEXT.secondary }}>{sp.count} {isHi ? "अध्याय" : "chapters"}</div>
                          </div>
                          {locked ? (
                            <span style={{ fontSize: fs(8), fontWeight: 700, background: C.surfaceCream, color: C.streakDark, padding: `${s(2)}px ${s(6)}px`, borderRadius: s(4) }}>PRO</span>
                          ) : (
                            <svg width={s(14)} height={s(14)} viewBox="0 0 24 24" fill="none" stroke={TEXT.muted} strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                          )}
                        </div>
                        {chapterNames.length > 0 && !locked && (
                          <div style={{ marginTop: s(6), paddingLeft: s(48), fontSize: fs(10), color: TEXT.muted, lineHeight: 1.6 }}>
                            {chapterNames.join(" · ")}{chapters.length > 3 ? ` · +${chapters.length - 3}` : ""}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </motion.div>
              )}

              {/* REVISE-EXAM-STEP-1-APPLIED: landing screen removed. 3 inline sections rendered directly in Revise tab */}
              {reviseView === "exam" && (
                <motion.div key="exam" {...fadeUp}>

                  {/* Section 1 — Test Yourself — REVISE-EXAM-STEP-2-APPLIED */}
                  <div className="animate-in stagger-0" style={{
                    background: "#FFFFFF", borderRadius: 20, padding: 20,
                    boxShadow: "0 2px 14px rgba(91,75,160,0.10)", marginBottom: 14,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: "rgba(26,122,82,0.08)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1A7A52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: "#3D2E15", fontFamily: ff }}>{isHi ? "खुद को परखें" : "Test yourself"}</div>
                        <div style={{ fontSize: 11, color: "#8B7355", fontFamily: ff }}>{isHi ? "MCQ क्विज़ · तुरंत स्कोर" : "MCQ quiz · Instant score"}</div>
                      </div>
                    </div>

                    {/* Subject chips */}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                      {subjectPills.length === 0 ? (
                        <div style={{ fontSize: 12, color: "#8B7355", fontFamily: ff }}>
                          {isHi ? "विषय लोड हो रहे हैं..." : "Loading subjects..."}
                        </div>
                      ) : subjectPills.map(sp => {
                        const selected = examSubj === sp.name;
                        return (
                          <div key={sp.name} className="tactile-pill" onClick={() => setExamSubj(sp.name)} style={{
                            padding: "7px 14px", borderRadius: 10,
                            fontSize: 12, fontWeight: 500, fontFamily: ff,
                            cursor: "pointer", minHeight: 36,
                            display: "flex", alignItems: "center",
                            background: selected ? "#5B4BA0" : "rgba(91,75,160,0.06)",
                            color: selected ? "#FFFFFF" : "#5B4BA0",
                            border: selected ? "none" : "1px solid rgba(91,75,160,0.12)",
                          }}>
                            {sp.name}
                          </div>
                        );
                      })}
                    </div>

                    {/* Config boxes */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                      <div style={{
                        flex: 1, background: "rgba(91,75,160,0.04)", borderRadius: 12,
                        padding: 10, textAlign: "center", border: "1px solid rgba(91,75,160,0.06)",
                      }}>
                        <div style={{ fontSize: 9, fontWeight: 600, color: "#8B7355", textTransform: "uppercase", letterSpacing: 0.5, fontFamily: ff }}>{isHi ? "सवाल" : "Questions"}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "#5B4BA0", marginTop: 2, fontFamily: ff }}>10</div>
                      </div>
                      <div style={{
                        flex: 1, background: "rgba(91,75,160,0.04)", borderRadius: 12,
                        padding: 10, textAlign: "center", border: "1px solid rgba(91,75,160,0.06)",
                      }}>
                        <div style={{ fontSize: 9, fontWeight: 600, color: "#8B7355", textTransform: "uppercase", letterSpacing: 0.5, fontFamily: ff }}>{isHi ? "समय" : "Time"}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "#5B4BA0", marginTop: 2, fontFamily: ff }}>15</div>
                        <div style={{ fontSize: 10, color: "#8B7355", fontFamily: ff }}>{isHi ? "मिनट" : "minutes"}</div>
                      </div>
                      <div style={{
                        flex: 1, background: "rgba(91,75,160,0.04)", borderRadius: 12,
                        padding: 10, textAlign: "center", border: "1px solid rgba(91,75,160,0.06)",
                      }}>
                        <div style={{ fontSize: 9, fontWeight: 600, color: "#8B7355", textTransform: "uppercase", letterSpacing: 0.5, fontFamily: ff }}>{isHi ? "प्रकार" : "Type"}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#5B4BA0", marginTop: 2, fontFamily: ff }}>MCQ</div>
                      </div>
                    </div>

                    {/* Start test CTA */}
                    <div
                      className="tactile-btn-primary"
                      onClick={() => {
                        // canUseTests() returns { allowed, reason, tier } — must read .allowed, not the object itself
                        if (!canUseTests().allowed) { setPaywallType("tests"); return; }
                        onExamCorner?.(examSubj);
                      }}
                      style={{
                        width: "100%",
                        background: "#1A7A52", color: "#FFFFFF",
                        borderRadius: 14, padding: "14px 0",
                        fontSize: 15, fontWeight: 600, fontFamily: ff,
                        textAlign: "center", cursor: "pointer",
                        borderBottom: "3px solid #15633F",
                      }}
                    >
                      {isHi ? "टेस्ट शुरू करें" : "Start test"}
                    </div>
                  </div>

                  {/* Section 2 — Board Questions — REVISE-EXAM-STEP-2-APPLIED */}
                  <div className="animate-in stagger-1" style={{
                    background: "#FFFFFF", borderRadius: 20, padding: 20,
                    boxShadow: "0 2px 14px rgba(91,75,160,0.10)", marginBottom: 14,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 10,
                          background: "rgba(91,75,160,0.08)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5B4BA0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M4 4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15z"/></svg>
                        </div>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: "#3D2E15", fontFamily: ff }}>{isHi ? "बोर्ड प्रश्न" : "Board questions"}</div>
                          <div style={{ fontSize: 11, color: "#8B7355", fontFamily: ff }}>{isHi ? "CBSE पिछले साल · अध्याय-वार" : "CBSE previous year · chapter-wise"}</div>
                        </div>
                      </div>
                      <div style={{
                        fontSize: 10, fontWeight: 600, color: "#E8A817",
                        background: "rgba(232,168,23,0.1)", borderRadius: 8, padding: "3px 10px", fontFamily: ff,
                      }}>SOON</div>
                    </div>

                    {["2024", "2023", "2022"].map((year, i, arr) => (
                      <div key={year} className="tactile-card" style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "12px 0",
                        borderBottom: i < arr.length - 1 ? "1px solid rgba(140,110,60,0.06)" : "none",
                      }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#3D2E15", fontFamily: ff }}>{isHi ? `${year} पेपर` : `${year} Paper`}</div>
                          <div style={{ fontSize: 11, color: "#8B7355", marginTop: 2, fontFamily: ff }}>{isHi ? "सभी विषय · 40 सवाल" : "All subjects · 40 questions"}</div>
                        </div>
                        <span style={{
                          fontSize: 9, fontWeight: 600, color: "#8B7355",
                          background: "rgba(140,110,60,0.06)", borderRadius: 6, padding: "2px 8px", fontFamily: ff,
                        }}>{isHi ? "जल्द ही" : "Coming soon"}</span>
                      </div>
                    ))}
                  </div>

                  {/* Section 3 — Test Results — REVISE-EXAM-STEP-2-APPLIED */}
                  <div className="animate-in stagger-2" style={{
                    background: "#FFFFFF", borderRadius: 20, padding: 20,
                    boxShadow: "0 2px 14px rgba(91,75,160,0.10)", marginBottom: 14,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 10,
                          background: "rgba(245,158,11,0.08)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E8A817" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg>
                        </div>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: "#3D2E15", fontFamily: ff }}>{isHi ? "टेस्ट के नतीजे" : "Test results"}</div>
                          <div style={{ fontSize: 11, color: "#8B7355", fontFamily: ff }}>{isHi ? "स्कोर और प्रदर्शन विश्लेषण" : "Scores and performance analysis"}</div>
                        </div>
                      </div>
                      <div style={{
                        fontSize: 10, fontWeight: 600, color: "#E8A817",
                        background: "rgba(232,168,23,0.1)", borderRadius: 8, padding: "3px 10px", fontFamily: ff,
                      }}>SOON</div>
                    </div>

                    <div style={{ textAlign: "center", padding: "24px 16px" }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: 14,
                        background: "rgba(245,158,11,0.06)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        margin: "0 auto 12px",
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E8A817" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/><line x1="3" y1="20" x2="21" y2="20"/></svg>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#3D2E15", marginBottom: 4, fontFamily: ff }}>
                        {isHi ? "आपके स्कोर यहाँ दिखेंगे" : "Your scores will appear here"}
                      </div>
                      <div style={{ fontSize: 12, color: "#8B7355", lineHeight: 1.5, fontFamily: ff }}>
                        {isHi ? "ऊपर टेस्ट दें और अपने नतीजे, कमज़ोर क्षेत्र और प्रगति देखें" : "Take a test above and track your results, weak areas, and progress"}
                      </div>
                    </div>
                  </div>

                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </>)}
      </div>

      {/* ═══ BOTTOM NAV — STUDY-REDESIGN: 4 tabs with Profile ═══ */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "#FFFFFF",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        padding: "8px 0 6px",
        boxShadow: "0 -1px 8px rgba(140,110,60,0.06)",
        zIndex: 50,
        paddingBottom: "max(6px, env(safe-area-inset-bottom))",
      }}>
        {[
          { id: "study", label: isHi ? "पढ़ें" : "Study", icon: (color) => <svg width={22} height={22} viewBox="0 0 24 24" fill={activeTab === "study" ? color : "none"} stroke={color} strokeWidth={activeTab === "study" ? 0 : 2} strokeLinecap="round"><path d="M21 5c-1.1-.3-2.3-.5-3.5-.5-1.9 0-4 .4-5.5 1.5-1.5-1.1-3.6-1.5-5.5-1.5S2.5 4.9 1 6v14.7c0 .2.2.4.5.4.1 0 .1 0 .2-.1C3.1 20.4 5 20 6.5 20c1.9 0 4 .4 5.5 1.5 1.3-.9 3.3-1.5 5.5-1.5 1.6 0 3.2.3 4.5.9.1.1.2.1.3.1.3 0 .5-.2.5-.4V6c-.5-.4-1.1-.8-1.8-1z"/></svg> },
          { id: "practice", label: isHi ? "AI ट्यूटर" : "AI Tutor", icon: (color) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
          { id: "revise", label: isHi ? "दोहराएँ" : "Revise", icon: (color) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
          { id: "profile", label: isHi ? "प्रोफ़ाइल" : "Profile", icon: (color) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
        ].map(t => {
          const active = activeTab === t.id;
          // CONTRAST-FIX 8: nav inactive 4.6:1 on white
          const color = active ? "#1A7A52" : "#8B7355";
          // AI-TUTOR-STEP-1C-APPLIED: practice tab routes directly to tutor (no landing)
          return (
            <div key={t.id} className="tactile-nav" onClick={() => {
              if (t.id === "profile") { onSettings?.(); return; }
              if (t.id === "practice") {
                // canUseAiChat() returns { allowed, reason, tier } — must read .allowed
                if (canUseAiChat().allowed) onMode("tutor");
                else setPaywallType("aiChat");
                return;
              }
              setActiveTab(t.id);
            }} style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              minWidth: 56, minHeight: 44, justifyContent: "center", cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}>
              {t.icon(color)}
              <span style={{ fontSize: 10, fontWeight: active ? 600 : 500, color: active ? "#1A7A52" : "#8B7355", fontFamily: ff }}>{t.label}</span>
              {active && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#1A7A52", marginTop: 1 }} />}
            </div>
          );
        })}
      </div>

      {/* Paywall */}
      {paywallType && <PaywallPopup type={paywallType} onClose={() => setPaywallType(null)} onUpgrade={onUpgrade} lang={lang} />}
    </div>
  );
}
